import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BodyRegularStartTransparentBlack900Important } from '../Text';
import styled from '@emotion/styled';
import { useAutocompleteSuggestions, AutocompleteSuggestion, AddressComponents } from '../../hooks';

// Constants
const MIN_INPUT_LENGTH = 2;
const DEFAULT_DEBOUNCE_MS = 600;

interface AutocompleteAddressInputProps {
  name: string;
  dataTestId: string;
  placeholder: string;
  disabled: boolean;
  onPlaceSelect: (place: AutocompleteSuggestion, addressComponents: AddressComponents | null) => void;
  componentRestrictions?: { country: string };
  state?: string;
  types?: string[];
  city?: string;
  value?: string;
  debounceMs?: number;
  onChange?: (value: string) => void;
}

const AutocompleteAddressInput: React.FC<AutocompleteAddressInputProps> = ({
  name,
  value,
  dataTestId,
  placeholder,
  disabled,
  onPlaceSelect,
  componentRestrictions,
  state,
  types,
  city,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<AutocompleteSuggestion[]>([]);
  const [shouldFetch, setShouldFetch] = useState(true);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const previousValueRef = useRef(value);
  const { fetchPredictions: originalFetchPredictions, getPlaceDetails } = useAutocompleteSuggestions();

  // Update inputValue when value prop changes from parent
  useEffect(() => {
    // Only update if the value actually changed from what we know
    if (value !== previousValueRef.current) {
      setInputValue(value);
      previousValueRef.current = value;
    }
  }, [value]);

  const fetchPredictions = useCallback(
    (input: string) => originalFetchPredictions(input, city, state, types, componentRestrictions),
    [originalFetchPredictions, city, state, types, componentRestrictions],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setInputValue(value);
    setShouldFetch(true);

    // Call onChange callback to update form decorator
    if (onChange) {
      onChange(value);
    }

    // Clear existing debounce timer
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (value.length > MIN_INPUT_LENGTH && shouldFetch) {
        fetchPredictions(value).then(setPredictions);
      }
    }, debounceMs);
  };

  const handleSelect = (prediction: AutocompleteSuggestion): void => {
    setPredictions([]);
    setShouldFetch(false); // Prevent API call after selection

    // Fetch detailed place information including parsed address components
    getPlaceDetails(prediction).then((addressComponents) => {
      onPlaceSelect(prediction, addressComponents);
    });
  };

  const handleBlur = (): void => {
    setPredictions([]); // Close dropdown when input loses focus
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  return (
    <InputContainer>
      <StyledInput
        type='text'
        placeholder={placeholder}
        disabled={disabled}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        aria-label='Address input with autocomplete'
        aria-expanded={predictions.length > 0}
        aria-autocomplete='list'
        role='combobox'
        data-testid={dataTestId}
      />
      {predictions.length > 0 && (
        <SuggestionsContainer role='listbox' aria-label='Address suggestions'>
          {predictions.map((pred, idx) => (
            <SuggestionItem
              key={pred.placePrediction?.id || idx}
              onMouseDown={(): void => handleSelect(pred)}
              role='option'
              aria-label={`${pred.placePrediction?.mainText?.text}${
                pred.placePrediction?.secondaryText?.text ? `, ${pred.placePrediction.secondaryText.text}` : ''
              }`}
            >
              {pred.placePrediction?.mainText?.text}
              {pred.placePrediction?.secondaryText?.text ? `, ${pred.placePrediction.secondaryText.text}` : ''}
            </SuggestionItem>
          ))}
        </SuggestionsContainer>
      )}
    </InputContainer>
  );
};

export default AutocompleteAddressInput;

const InputContainer = styled.div`
  min-height: 54px;
  margin: 0 15px;
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{ disabled?: boolean }>`
  height: auto;
  padding: 1px 0;
  box-sizing: border-box;
  width: 100%;
  border: none;
  outline: none;
  font-size: 16px;
  font-weight: normal;
  ${BodyRegularStartTransparentBlack900Important.css};
  color: ${({ disabled }): string => (disabled ? 'var(--transparent-black-400) !important' : 'inherit')};

  &::placeholder {
    color: var(--transparent-black-400) !important;
  }

  &::-webkit-input-placeholder {
    color: var(--transparent-black-400) !important;
  }

  &::-moz-placeholder {
    color: var(--transparent-black-400) !important;
  }

  &:-ms-input-placeholder {
    color: var(--transparent-black-400) !important;
  }
`;

const SuggestionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
`;

const SuggestionItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 16px;
  line-height: 1.5;
  color: #333;

  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
`;
